import './sentry'
// Registers the self-hosted <altcha-widget> custom element used by the public
// forms (proof-of-work spam protection; no third-party account). The default
// 'altcha' bundle spawns its PoW worker from a `blob:` URL, which our CSP
// (script-src with no worker-src/blob: allowance) blocks. 'altcha/external'
// skips that auto-registration so we can wire up the SHA-256 worker (the only
// algorithm AltchaService issues, see app/Services/AltchaService.php) as a
// same-origin file via Vite's `?worker` import instead.
import 'altcha/external'
// The default 'altcha' bundle auto-injects the widget's stylesheet; the
// 'altcha/external' bundle does not, so its CSS must be imported explicitly or
// the <altcha-widget> renders as unstyled light-DOM HTML.
import 'altcha/altcha.css'
import ShaWorker from 'altcha/workers/sha?worker'

$altcha.algorithms.set('SHA-256', () => new ShaWorker())

// ── Types ──────────────────────────────────────────────────────────────────────

type ValidationRule = (v: string) => string | null
type ValidationField = [inputId: string, errorId: string, rule: ValidationRule]

// ── Hero chart ─────────────────────────────────────────────────────────────────

/**
 * The hero chart shows the SAME live Nord Pool HPFC curve as the "Live curve
 * preview" section below — fed by GET /api/curves/preview (the curve identified
 * by CURVE_SOURCE_ID). It is a decorative mini-chart drawn straight into the
 * existing hero SVG (`#hero-chart-line` / `#hero-chart-area`), so there is a
 * single data source and no stale hardcoded shape or dates.
 */

type HeroResolution = 'hour' | 'day' | 'month'

/** Hero tab label → API resolution. */
const HERO_RESOLUTION: Record<string, HeroResolution> = {
    Hourly: 'hour',
    Daily: 'day',
    Monthly: 'month',
}

interface HeroCurvePoint {
    date: string
    value: number
}

interface HeroCurveSnapshot {
    points: HeroCurvePoint[]
}

/** The hero SVG's coordinate geometry (viewBox 0 0 480 170). */
const HERO_PLOT = {
    left: 40,
    right: 470,
    top: 10,
    bottom: 130,
    baseline: 155, // area fill closes to here (just above the x-axis labels)
    yTicks: [13, 43, 73, 103, 133], // baseline `y` attrs of the 5 grid labels
    xTickXs: [60, 130, 200, 270, 340, 410, 460], // `x` attrs of the 7 date ticks
} as const

const heroUtc = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: 'UTC' })

const heroParseUtc = (s: string): Date =>
    new Date(/^\d{4}-\d{2}-\d{2} /.test(s) ? s.replace(' ', 'T') + 'Z' : s)

function heroTickFormatter(resolution: HeroResolution): (d: Date) => string {
    if (resolution === 'hour') {
        return heroUtc({ day: '2-digit', month: 'short' }).format
    }
    if (resolution === 'day') {
        return heroUtc({ day: '2-digit', month: 'short' }).format
    }
    return heroUtc({ month: 'short', year: '2-digit' }).format
}

interface HeroPaths {
    line: string
    fill: string
    /** Top-to-bottom y-axis value labels aligned with `HERO_PLOT.yTicks`. */
    yLabels: string[]
    /** Left-to-right x-axis date labels aligned with `HERO_PLOT.xTickXs`. */
    xLabels: string[]
}

/**
 * The hero SVG plot is only ~430px wide and purely decorative, so an 8,760-point
 * hourly year would render as a dense filled blob, not a readable curve. Thin to
 * at most this many points — keeping every emitted point a *real* source point at
 * its *true* timestamp (uniform index stride, first + last preserved) — so the
 * hero reads as a clean shape while still reflecting the live data honestly.
 */
const HERO_MAX_POINTS = 160

export function thinForHero(data: [number, number][], maxPoints = HERO_MAX_POINTS): [number, number][] {
    if (data.length <= maxPoints) {
        return data
    }
    const stride = Math.ceil((data.length - 1) / (maxPoints - 1))
    const result: [number, number][] = []
    for (let i = 0; i < data.length; i += stride) {
        result.push(data[i])
    }
    const last = data[data.length - 1]
    if (result[result.length - 1] !== last) {
        result.push(last)
    }
    return result
}

/**
 * Map real `[timestampMs, value]` points into the hero SVG coordinate space and
 * build the line path, the closed area path, and the dynamic axis labels. Points
 * are anchored to their true position in time (x) and value (y) — no smoothing,
 * no fabricated shape — so the hero faithfully reflects the live curve (thinned
 * for the small decorative plot, never re-timestamped). Returns `null` when there
 * aren't enough points to draw a line.
 */
export function buildHeroPaths(
    rawPoints: [number, number][],
    resolution: HeroResolution,
): HeroPaths | null {
    if (rawPoints.length < 2) {
        return null
    }

    const points = thinForHero(rawPoints)
    const { left, right, top, bottom, baseline } = HERO_PLOT
    const tMin = points[0][0]
    const tMax = points[points.length - 1][0]
    const tSpan = tMax - tMin || 1
    let vMin = Infinity
    let vMax = -Infinity
    for (const [, v] of points) {
        if (v < vMin) vMin = v
        if (v > vMax) vMax = v
    }
    // Pad the value range so the line never sits flush against the top/bottom.
    const vPad = (vMax - vMin) * 0.08 || 1
    vMin -= vPad
    vMax += vPad
    const vSpan = vMax - vMin || 1

    const px = (t: number) => left + ((t - tMin) / tSpan) * (right - left)
    const py = (v: number) => bottom - ((v - vMin) / vSpan) * (bottom - top)

    const coords = points.map(([t, v]) => `${px(t).toFixed(1)},${py(v).toFixed(1)}`)
    const line = `M${coords.join(' L')}`
    const fill = `${line} L${right.toFixed(1)},${baseline} L${left.toFixed(1)},${baseline} Z`

    // Y labels: top tick = vMax, bottom tick = vMin, evenly spaced in value.
    const yLabels = HERO_PLOT.yTicks.map((_, i) => {
        const frac = i / (HERO_PLOT.yTicks.length - 1) // 0 (top) … 1 (bottom)
        return String(Math.round(vMax - frac * (vMax - vMin)))
    })

    // X labels: evenly spaced in time across the visible range.
    const fmt = heroTickFormatter(resolution)
    const xLabels = HERO_PLOT.xTickXs.map((_, i) => {
        const frac = i / (HERO_PLOT.xTickXs.length - 1)
        return fmt(new Date(tMin + frac * tSpan))
    })

    return { line, fill, yLabels, xLabels }
}

function animateHeroChart(lineEl: SVGPathElement | null, areaEl: SVGPathElement | null): void {
    if (!lineEl) return
    const len = lineEl.getTotalLength()
    lineEl.style.setProperty('--line-len', String(len))
    lineEl.style.strokeDasharray = String(len)
    lineEl.style.strokeDashoffset = String(len)
    if (areaEl) areaEl.style.opacity = '0'
    lineEl.style.animation = 'none'
    lineEl.getBoundingClientRect()
    lineEl.style.animation = 'drawLine 1.8s cubic-bezier(0.4,0,0.2,1) forwards'
    if (areaEl) {
        areaEl.style.animation = 'none'
        areaEl.getBoundingClientRect()
        areaEl.style.animation = 'fadeArea 0.5s ease-out 1.6s forwards'
    }
}

/** Apply built hero paths + dynamic axis labels into the hero SVG, then animate. */
function applyHeroPaths(card: Element, paths: HeroPaths): void {
    const lineEl = document.getElementById('hero-chart-line') as SVGPathElement | null
    const areaEl = document.getElementById('hero-chart-area') as SVGPathElement | null
    lineEl?.setAttribute('d', paths.line)
    areaEl?.setAttribute('d', paths.fill)

    const ticks = Array.from(card.querySelectorAll('.chart-axis-tick'))
    ticks
        .filter(el => HERO_PLOT.yTicks.includes(Number(el.getAttribute('y'))))
        .forEach((el, i) => { if (paths.yLabels[i] !== undefined) el.textContent = paths.yLabels[i] })
    ticks
        .filter(el => el.getAttribute('y') === '165')
        .forEach((el, i) => { if (paths.xLabels[i] !== undefined) el.textContent = paths.xLabels[i] })

    animateHeroChart(lineEl, areaEl)
}

/** Cache fetched snapshots per resolution so tab switches don't re-fetch. */
const heroCache = new Map<HeroResolution, [number, number][]>()
let heroRequestSeq = 0

async function fetchHeroPoints(resolution: HeroResolution): Promise<[number, number][] | null> {
    const cached = heroCache.get(resolution)
    if (cached) return cached
    try {
        const res = await fetch(`/api/curves/preview?resolution=${resolution}`, {
            headers: { Accept: 'application/json' },
        })
        if (!res.ok) return null
        const snapshot = (await res.json()) as HeroCurveSnapshot
        const points = snapshot.points.map(
            (p) => [heroParseUtc(p.date).getTime(), Number(p.value)] as [number, number],
        )
        heroCache.set(resolution, points)
        return points
    } catch {
        return null
    }
}

async function loadHeroChart(card: Element, resolution: HeroResolution): Promise<void> {
    const seq = ++heroRequestSeq

    let used = resolution
    let points = await fetchHeroPoints(resolution)
    // The requested resolution can be 503/404 early in the day (e.g. hourly not
    // yet published); fall back to Monthly — the API's own default and the most
    // likely to be present — so the prominent hero never renders blank.
    if (!points && resolution !== 'month') {
        used = 'month'
        points = await fetchHeroPoints('month')
    }

    if (seq !== heroRequestSeq) return // a newer tab click won
    if (!points) return // nothing available — leave the placeholder in place

    const paths = buildHeroPaths(points, used)
    if (paths) applyHeroPaths(card, paths)
}

function setChartTab(btn: HTMLElement): void {
    const card = btn.closest('.hero-chart-card')
    if (!card) return
    card.querySelectorAll('.chart-tab').forEach(t => t.classList.remove('active'))
    btn.classList.add('active')
    const resolution = HERO_RESOLUTION[btn.textContent?.trim() ?? '']
    if (!resolution) return
    void loadHeroChart(card, resolution)
}

// ── Scroll reveal ──────────────────────────────────────────────────────────────

function makeRevealObserver(selector: string, threshold: number): void {
    const items = document.querySelectorAll(selector)
    if (!items.length) return
    const obs = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('revealed')
                obs.unobserve(entry.target)
            }
        })
    }, { threshold })
    items.forEach(el => obs.observe(el))
}

// ── Download modal ─────────────────────────────────────────────────────────────

function openDownloadModal(): void {
    const modal = document.getElementById('downloadModal') as HTMLElement | null
    const form = document.getElementById('downloadForm') as HTMLFormElement | null
    const confirm = document.getElementById('downloadConfirm') as HTMLElement | null
    if (!modal || !form || !confirm) return
    modal.style.display = 'flex'
    form.reset()
    confirm.style.display = 'none'
    form.style.display = 'block'
}

function closeDownloadModal(): void {
    const modal = document.getElementById('downloadModal') as HTMLElement | null
    if (modal) modal.style.display = 'none'
}

// ── Validation ─────────────────────────────────────────────────────────────────

const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
    'live.com', 'aol.com', 'protonmail.com', 'mail.com', 'gmx.com', 'yandex.com',
])

const RULES: Record<string, ValidationRule> = {
    name(v) {
        if (!v.trim()) return 'Name is required.'
        if (v.trim().length < 2) return 'Name must be at least 2 characters.'
        if (!/^[\p{L}\s'\-.]+$/u.test(v.trim())) return 'Name contains invalid characters.'
        return null
    },
    corporateEmail(v) {
        if (!v.trim()) return 'Email is required.'
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())) return 'Enter a valid email address.'
        const domain = v.trim().split('@')[1].toLowerCase()
        if (FREE_EMAIL_DOMAINS.has(domain)) return 'Please use a corporate email address.'
        return null
    },
    phone(v) {
        if (!v.trim()) return 'Phone number is required.'
        if (!/^\+?[\d\s\-(). ]{7,20}$/.test(v.trim())) return 'Enter a valid phone number (e.g. +49 123 456 789).'
        return null
    },
    text(v) {
        if (!v.trim()) return 'This field is required.'
        return null
    },
}

function applyError(
    input: HTMLInputElement | HTMLTextAreaElement,
    errorSpan: HTMLElement,
    message: string | null,
): boolean {
    const invalid = Boolean(message)
    input.style.borderColor = invalid ? '#ef4444' : ''
    errorSpan.textContent = message ?? ''
    return invalid
}

function wireBlur(inputId: string, errorId: string, rule: ValidationRule): void {
    const input = document.getElementById(inputId) as HTMLInputElement | HTMLTextAreaElement | null
    const span = document.getElementById(errorId)
    if (!input || !span) return
    input.addEventListener('blur', () => applyError(input, span, rule(input.value)))
    input.addEventListener('input', () => { if (span.textContent) applyError(input, span, rule(input.value)) })
}

function validateForm(fields: ValidationField[]): boolean {
    return fields
        .map(([inputId, errorId, rule]) => {
            const input = document.getElementById(inputId) as HTMLInputElement | HTMLTextAreaElement | null
            const span = document.getElementById(errorId)
            if (!input || !span) return true
            return applyError(input, span, rule(input.value))
        })
        .every(invalid => !invalid)
}

function handleFormSubmit(e: Event): void {
    const valid = validateForm([
        ['lead-name',    'lead-name-error',    RULES.name],
        ['lead-email',   'lead-email-error',   RULES.corporateEmail],
        ['lead-phone',   'lead-phone-error',   RULES.phone],
        ['lead-message', 'lead-message-error', RULES.text],
    ])
    if (!valid) {
        e.preventDefault()
        return
    }
    // Hold the native submit until ALTCHA has produced its token, otherwise the
    // server rejects the post. Once solved, submit natively so the server can
    // redirect back with session('success') and Blade swaps in #form-success.
    const form = e.currentTarget as HTMLFormElement
    const widget = altchaWidget(form)
    if (!widget || widget.value) return
    e.preventDefault()
    void altchaSolved(widget).then(ok => {
        if (ok) {
            form.submit()
        } else {
            const err = document.getElementById('lead-altcha-error')
            if (err) err.textContent = 'Captcha verification failed — please try again.'
        }
    })
}

async function handleDownloadSubmit(e: Event): Promise<void> {
    e.preventDefault()
    const valid = validateForm([
        ['dl-name',  'dl-name-error',  RULES.name],
        ['dl-email', 'dl-email-error', RULES.corporateEmail],
    ])
    if (!valid) return
    const form = document.getElementById('downloadForm') as HTMLFormElement | null
    if (!form) return
    // Wait for the ALTCHA token before posting — FormData(form) then carries the
    // hidden `altcha` field the server requires.
    const widget = altchaWidget(form)
    if (widget && !widget.value) {
        const ok = await altchaSolved(widget)
        if (!ok) {
            const span = document.getElementById('dl-email-error')
            if (span) span.textContent = 'Captcha verification failed — please try again.'
            return
        }
    }
    const resp = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    if (!resp.ok) {
        const span = document.getElementById('dl-email-error')
        if (span) span.textContent = await firstServerError(resp)
        return
    }
    const confirm = document.getElementById('downloadConfirm')
    form.style.display = 'none'
    if (confirm) confirm.style.display = 'block'
}

/** First message from a Laravel 422 validation payload, or a generic fallback. */
async function firstServerError(resp: Response): Promise<string> {
    if (resp.status === 422) {
        const data = await resp.json().catch(() => null) as { errors?: Record<string, string[]> } | null
        const first = data?.errors ? Object.values(data.errors).flat()[0] : null
        if (typeof first === 'string') return first
    }
    return 'Something went wrong — please try again later.'
}

// ── ALTCHA proof-of-work captcha ─────────────────────────────────────────────────
// The <altcha-widget> (registered by `import 'altcha'`) solves a proof-of-work
// challenge on load and exposes the token via its `value` property plus a hidden
// `altcha` form field. These helpers let the submit handlers wait for that token
// so a form never posts before verification finishes. When ALTCHA is disabled the
// widget isn't rendered and `altchaWidget()` returns null, so the forms behave as
// they did before.

interface AltchaWidget extends HTMLElement {
    value?: string
    verify?: () => void
}

/**
 * Latest `statechange` state per widget, so a submit can distinguish "still
 * solving" from "expired / idle" and avoid interrupting an in-flight solve.
 * Populated by trackAltchaWidgets().
 */
const altchaStates = new WeakMap<AltchaWidget, string>()

function altchaWidget(form: HTMLElement): AltchaWidget | null {
    return form.querySelector<AltchaWidget>('altcha-widget')
}

function trackAltchaWidgets(): void {
    document.querySelectorAll<AltchaWidget>('altcha-widget').forEach(widget => {
        widget.addEventListener('statechange', e => {
            const state = (e as CustomEvent<{ state?: string }>).detail?.state
            if (state) altchaStates.set(widget, state)
        })
    })
}

/**
 * Resolve true once the widget holds a token, false if verification fails or
 * stalls. With auto="onload" the widget solves once and does NOT re-solve after
 * its token expires, so we (re)trigger verify() when it isn't already solving,
 * re-solve on `expired`, and time out rather than hang on a stuck challenge fetch.
 */
function altchaSolved(widget: AltchaWidget): Promise<boolean> {
    if (widget.value) return Promise.resolve(true)
    return new Promise<boolean>(resolve => {
        let settled = false
        const finish = (ok: boolean): void => {
            if (settled) return
            settled = true
            widget.removeEventListener('statechange', onState)
            clearTimeout(timer)
            resolve(ok)
        }
        const onState = (e: Event): void => {
            const state = (e as CustomEvent<{ state?: string }>).detail?.state
            if (state === 'verified' || widget.value) finish(true)
            else if (state === 'error') finish(false)
            else if (state === 'expired') widget.verify?.()
        }
        widget.addEventListener('statechange', onState)
        // Kick a (re)solve unless one is already in flight — covers an expired or
        // idle widget, which auto="onload" would otherwise never re-solve.
        if (altchaStates.get(widget) !== 'verifying') widget.verify?.()
        const timer = setTimeout(() => finish(false), 20000)
        // Guard the race where verification completed between the value check
        // above and attaching the listener.
        if (widget.value) finish(true)
    })
}

// ── FAQ ────────────────────────────────────────────────────────────────────────

function toggleFaq(btn: HTMLElement): void {
    const item = btn.parentElement
    if (!item) return
    const isOpen = item.classList.contains('open')
    document.querySelectorAll('#faqList .faq-item').forEach(el => el.classList.remove('open'))
    if (!isOpen) item.classList.add('open')
}

// ── Init ───────────────────────────────────────────────────────────────────────

function init(): void {
    // Hero chart tabs
    document.querySelectorAll<HTMLElement>('.chart-tab').forEach(btn =>
        btn.addEventListener('click', () => setChartTab(btn))
    )

    // Download modal open/close
    document.querySelectorAll<HTMLElement>('.preview-download').forEach(btn =>
        btn.addEventListener('click', openDownloadModal)
    )
    const modal = document.getElementById('downloadModal')
    modal?.addEventListener('click', e => { if (e.target === modal) closeDownloadModal() })
    document.getElementById('download-modal-close')?.addEventListener('click', closeDownloadModal)

    // Forms
    trackAltchaWidgets()
    document.getElementById('lead-form')?.addEventListener('submit', handleFormSubmit)
    document.getElementById('downloadForm')?.addEventListener('submit', handleDownloadSubmit)

    // FAQ
    document.querySelectorAll<HTMLElement>('.faq-q').forEach(btn =>
        btn.addEventListener('click', () => toggleFaq(btn))
    )

    // Blur validation — lead form
    wireBlur('lead-name',    'lead-name-error',    RULES.name)
    wireBlur('lead-email',   'lead-email-error',   RULES.corporateEmail)
    wireBlur('lead-phone',   'lead-phone-error',   RULES.phone)
    wireBlur('lead-message', 'lead-message-error', RULES.text)

    // Blur validation — download modal
    wireBlur('dl-name',  'dl-name-error',  RULES.name)
    wireBlur('dl-email', 'dl-email-error', RULES.corporateEmail)

    // Hero chart: load the active tab's live Nord Pool HPFC curve (same source
    // as the live-preview section). Falls back to the inline placeholder shape
    // if the request fails. animateHeroChart runs once the live paths land.
    const heroCard = document.querySelector('.hero-chart-card')
    if (heroCard) {
        const activeTab = heroCard.querySelector<HTMLElement>('.chart-tab.active')
        const resolution = HERO_RESOLUTION[activeTab?.textContent?.trim() ?? ''] ?? 'hour'
        void loadHeroChart(heroCard, resolution)
    }

    // Scroll reveal
    makeRevealObserver('.why-card.reveal-item', 0.15)
    makeRevealObserver('.kyos-stat.reveal-item', 0.2)
    makeRevealObserver('.kyos-logos.reveal-item', 0.2)
}

// Guarded so the module's pure helpers (e.g. buildHeroPaths) can be imported in
// a non-DOM (test) environment without running the DOM bootstrap.
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init)
    } else {
        init()
    }
}
